import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Eye, KeyRound, LogIn, Mail, Pause, Play, Trash2 } from 'lucide-react'
import { ConfirmDialog } from './ui/ConfirmDialog'
import { Modal } from './ui/Modal'
import { Button } from './ui/Button'
import { api, extractApiError } from '../lib/api'
import { setFlashToast } from '../lib/flash'
import { useAuthStore } from '../stores/authStore'
import { CopyableUrl } from './TenantRowActions'
import { PlatformUserDetailModal } from './PlatformUserDetailModal'
import { RowActionsMenu } from './table/RowActionsMenu'

/**
 * Platform Admin row actions for a single user on the "All Users" page.
 *
 * Renders as a cluster of inline icon buttons — same shape and density
 * as `TenantRowActions` on the Companies page, so the Actions column
 * reads the same across every platform-admin list. Previously this
 * was a portal-positioned kebab menu, which made the All Users
 * Actions column visually inconsistent with the rest of the platform
 * (Companies, Plans, etc. all use inline icons).
 *
 * Status-aware so the cluster doesn't offer nonsensical actions:
 * Suspend hides on an already-suspended user, Resend invite shows
 * only for invited accounts, Force reset hides for invited accounts.
 *
 * Platform Admin rows are read-only — every action is gated server-
 * side with a 403 — and the entire cluster is replaced with an
 * empty slot so the table grid doesn't reflow.
 */
export function PlatformUserRowActions({ user }) {
  const qc = useQueryClient()
  const [detailOpen, setDetailOpen] = useState(false)
  const [confirmSuspend, setConfirmSuspend] = useState(false)
  const [confirmReactivate, setConfirmReactivate] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [confirmForceReset, setConfirmForceReset] = useState(false)
  const [confirmImpersonate, setConfirmImpersonate] = useState(false)
  const setImpersonation = useAuthStore((s) => s.setImpersonation)
  // The resend-invite + force-reset actions both return a setup/reset
  // URL the admin can copy and hand over as a fallback to email. We
  // surface those in tiny follow-up modals using the same pattern as
  // the tenant flow.
  const [inviteResult, setInviteResult] = useState(null)
  const [resetResult, setResetResult] = useState(null)

  function invalidate() {
    void qc.invalidateQueries({ queryKey: ['platform-users'] })
    void qc.invalidateQueries({ queryKey: ['platform-overview'] })
  }

  const suspendMutation = useMutation({
    mutationFn: async () => {
      await api.patch(`/super-admin/platform/users/${user.id}/status`, {
        status: 'suspended',
      })
    },
    onSuccess: () => {
      toast.success(`"${user.fullName}" suspended.`)
      setConfirmSuspend(false)
      invalidate()
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const reactivateMutation = useMutation({
    mutationFn: async () => {
      await api.patch(`/super-admin/platform/users/${user.id}/status`, {
        status: 'active',
      })
    },
    onSuccess: () => {
      toast.success(`"${user.fullName}" reactivated.`)
      setConfirmReactivate(false)
      invalidate()
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const res = await api.delete(`/super-admin/platform/users/${user.id}`)
      return res.data
    },
    onSuccess: (data) => {
      toast.success(data?.message ?? `"${user.fullName}" deleted successfully.`)
      setConfirmDelete(false)
      invalidate()
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const resendMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post(
        `/super-admin/platform/users/${user.id}/resend-invite`,
      )
      return res.data
    },
    onSuccess: (data) => {
      toast.success(data?.message ?? 'Invitation re-sent.')
      setInviteResult(data)
      invalidate()
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const forceResetMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post(
        `/super-admin/platform/users/${user.id}/force-password-reset`,
      )
      return res.data
    },
    onSuccess: (data) => {
      toast.success(data?.message ?? 'Password reset link sent.')
      setConfirmForceReset(false)
      setResetResult(data)
      invalidate()
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const impersonateMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post(
        `/super-admin/platform/users/${user.id}/impersonate`,
      )
      return res.data
    },
    onSuccess: (data) => {
      // Prime the banner immediately so it shows before the post-
      // navigation /auth/me round-trip resolves.
      setImpersonation(data?.impersonation ?? null)
      // Queue the success toast to fire AFTER the reload below — a toast here
      // would be wiped by the hard navigation before it ever paints.
      setFlashToast(`Successfully impersonating ${user.fullName}.`)
      // Hard navigate so React Query caches scoped to the Super Admin's
      // own session don't leak into the impersonated user's view — and
      // so the app re-bootstraps under the borrowed cookies.
      window.location.href = '/'
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  // Platform Admins are read-only here — surface a stable empty slot so
  // the table grid doesn't reflow rows depending on role.
  if (user.role === 'super_admin') {
    return <span aria-hidden="true" />
  }

  const isInvited = user.status === 'invited'
  const isActive = user.status === 'active'
  const isSuspended = user.status === 'suspended'
  const isOffboarded = user.status === 'offboarded'

  return (
    <>
      {/* Kebab (⋮) menu — the DataTable Actions-column standard. Order:
       *  View → conditional middle (Resend invite when invited, Force
       *  reset otherwise) → Suspend / Reactivate → Delete (red, behind a
       *  divider). Activity log stays inside the View-details modal so
       *  the menu stays short. Falsy entries drop out automatically. */}
      <RowActionsMenu
        label={`Actions for ${user.fullName}`}
        items={[
          { key: 'view', label: 'View details', icon: Eye, onClick: () => setDetailOpen(true) },
          isActive && {
            key: 'impersonate',
            label: 'Impersonate',
            icon: LogIn,
            onClick: () => setConfirmImpersonate(true),
            disabled: impersonateMutation.isPending,
          },
          isInvited
            ? {
                key: 'resend',
                label: 'Resend invite',
                icon: Mail,
                onClick: () => resendMutation.mutate(),
                disabled: resendMutation.isPending,
              }
            : // Offboarded users can no longer sign in, so a password reset
              // is meaningless — omit it (re-onboarding is the path back).
              isOffboarded
              ? null
              : {
                  key: 'reset',
                  label: 'Force Password Reset',
                  icon: KeyRound,
                  onClick: () => setConfirmForceReset(true),
                },
          isActive && {
            key: 'suspend',
            label: 'Suspend',
            icon: Pause,
            onClick: () => setConfirmSuspend(true),
          },
          isSuspended && {
            key: 'reactivate',
            label: 'Reactivate',
            icon: Play,
            onClick: () => setConfirmReactivate(true),
          },
          {
            key: 'delete',
            label: 'Delete',
            icon: Trash2,
            danger: true,
            separatorBefore: true,
            onClick: () => setConfirmDelete(true),
          },
        ]}
      />

      <PlatformUserDetailModal
        userId={detailOpen ? user.id : null}
        onClose={() => setDetailOpen(false)}
      />

      <ConfirmDialog
        open={confirmImpersonate}
        title="Impersonate User"
        message={`Sign in as "${user.fullName}" to view the platform exactly as they see it? A banner stays visible the whole time, and every action is recorded against their account. Changing their password and moving money on their behalf stay blocked. You can exit impersonation at any time.`}
        confirmLabel="Impersonate"
        variant="primary"
        confirming={impersonateMutation.isPending}
        onCancel={() => setConfirmImpersonate(false)}
        onConfirm={() => impersonateMutation.mutate()}
      />

      <ConfirmDialog
        open={confirmForceReset}
        title="Force Password Reset"
        message={`Send "${user.fullName}" a password-reset link and sign them out everywhere? Their current password keeps working until they click the link, but every signed-in tab is evicted immediately.`}
        confirmLabel="Send Reset Link"
        variant="primary"
        confirming={forceResetMutation.isPending}
        onCancel={() => setConfirmForceReset(false)}
        onConfirm={() => forceResetMutation.mutate()}
      />

      <ConfirmDialog
        open={confirmSuspend}
        title="Suspend User"
        message={`Suspending "${user.fullName}" will immediately sign them out everywhere and block sign-in until you reactivate. Continue?`}
        confirmLabel="Suspend"
        variant="danger"
        confirming={suspendMutation.isPending}
        onCancel={() => setConfirmSuspend(false)}
        onConfirm={() => suspendMutation.mutate()}
      />

      <ConfirmDialog
        open={confirmReactivate}
        title="Reactivate User"
        message={`Reactivate "${user.fullName}"? They'll be able to sign in again.`}
        confirmLabel="Reactivate"
        variant="primary"
        confirming={reactivateMutation.isPending}
        onCancel={() => setConfirmReactivate(false)}
        onConfirm={() => reactivateMutation.mutate()}
      />

      <ConfirmDialog
        open={confirmDelete}
        title="Delete User"
        message={`Are you sure you want to delete "${user.fullName}"? This will permanently remove all their data — tasks they're assigned to, time entries, reports, points, badges, notifications and workspace memberships. This action cannot be undone.`}
        confirmLabel="Delete Permanently"
        variant="danger"
        confirming={deleteMutation.isPending}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => deleteMutation.mutate()}
      />

      <Modal
        open={inviteResult !== null}
        onClose={() => setInviteResult(null)}
        title="Invitation Re-sent"
        description="The user has been emailed a fresh password-setup link. You can also copy the link below as a fallback."
        size="md"
        footer={<Button onClick={() => setInviteResult(null)}>Done</Button>}
      >
        {inviteResult ? (
          <div className="space-y-3">
            <p className="text-sm text-[color:var(--color-ink)]">
              Sent to <strong>{inviteResult.user?.emailId}</strong>.{' '}
              {inviteResult.setupLinkExpiresIn
                ? `The link expires in ${inviteResult.setupLinkExpiresIn} and can only be used once.`
                : 'The link can only be used once.'}
            </p>
            <CopyableUrl url={inviteResult.setupUrl} />
          </div>
        ) : null}
      </Modal>

      <Modal
        open={resetResult !== null}
        onClose={() => setResetResult(null)}
        title="Password Reset Link Sent"
        description="All sessions have been signed out and the user has been emailed a one-time reset link."
        size="md"
        footer={<Button onClick={() => setResetResult(null)}>Done</Button>}
      >
        {resetResult ? (
          <div className="space-y-3">
            <p className="text-sm text-[color:var(--color-ink)]">
              Sent to <strong>{resetResult.user?.emailId}</strong>.{' '}
              {resetResult.resetLinkExpiresIn
                ? `The link expires in ${resetResult.resetLinkExpiresIn} and can only be used once.`
                : 'The link can only be used once.'}
            </p>
            <CopyableUrl url={resetResult.resetUrl} />
          </div>
        ) : null}
      </Modal>
    </>
  )
}
